import { collection, doc, getDoc, getDocs, updateDoc, deleteDoc, setDoc, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const NOTIFICATIONS_COLLECTION = 'notifications';

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const asUuid = (v) => (typeof v === 'string' && UUID_RE.test(v) ? v : null);

// iOS decodes dates with ISO8601DateFormatter (.withInternetDateTime) which does
// NOT accept fractional seconds — strip ".SSS" so the string parses on iOS.
const toIsoString = (d) => d.toISOString().replace(/\.\d{3}Z$/, 'Z');

// iOS queries notifications by the parent/user record's `uuid` field
// (NotificationService.swift: whereField("parentId", isEqualTo: uuid)).
// Web callers pass the Firebase Auth uid — resolve it to the stored `uuid`.
async function resolveParentUuid(parentId) {
  if (!parentId) return null;
  if (asUuid(parentId)) return parentId;
  try {
    const parentSnap = await getDoc(doc(db, 'parents', parentId));
    const parentUuid = parentSnap.exists() ? asUuid(parentSnap.data().uuid) : null;
    if (parentUuid) return parentUuid;
  } catch (e) { /* fall through to users lookup */ }
  try {
    const userSnap = await getDoc(doc(db, 'users', parentId));
    const userUuid = userSnap.exists() ? asUuid(userSnap.data().uuid) : null;
    if (userUuid) return userUuid;
  } catch (e) { /* keep original value */ }
  return parentId;
}

export const NotificationType = {
  NATIONAL: 'national',
  SCHOOL: 'school',
  NEW_EVENT: 'new_event',
  GENERAL: 'general',
  NEW_ASSIGNMENT: 'new_assignment',
  ASSIGNMENT_DUE_SOON: 'assignment_due_soon',
  ASSIGNMENT_OVERDUE: 'assignment_overdue',
  ASSIGNMENT_GRADED: 'assignment_graded',
  ONLINE_CLASS_REMINDER: 'online_class_reminder',
  ONLINE_CLASS_STARTING: 'online_class_starting',
  ONLINE_CLASS_STARTED: 'online_class_started',
  ONLINE_CLASS_ENDED: 'online_class_ended',
  NEW_LEARNING_MATERIAL: 'new_learning_material',
  TEACHER_ANNOUNCEMENT: 'teacher_announcement',
  ATTENDANCE_MARKED: 'attendance_marked',
  ATTENDANCE_LATE: 'attendance_late',
  ATTENDANCE_ABSENT: 'attendance_absent',
  SCAN_ARRIVAL: 'scan_arrival',
  SCAN_DEPARTURE: 'scan_departure',
  SCAN_BUS_BOARDING: 'scan_bus_boarding',
  SCAN_BUS_DROP_OFF: 'scan_bus_drop_off',
  SCAN_GENERAL: 'scan_general',
  GRADE_CHANGE_APPROVED: 'grade_change_approved',
  GRADE_CHANGE_REJECTED: 'grade_change_rejected',
};

export const NotificationTypeInfo = {
  [NotificationType.NATIONAL]: { icon: '🏛️', color: '#7c3aed', label: 'National' },
  [NotificationType.SCHOOL]: { icon: '🏫', color: '#22c55e', label: 'School' },
  [NotificationType.NEW_EVENT]: { icon: '🎉', color: '#22c55e', label: 'New Event' },
  [NotificationType.GENERAL]: { icon: 'ℹ️', color: '#6b7280', label: 'General' },
  [NotificationType.NEW_ASSIGNMENT]: { icon: '📝', color: '#f59e0b', label: 'New Assignment' },
  [NotificationType.ASSIGNMENT_DUE_SOON]: { icon: '⏰', color: '#f59e0b', label: 'Assignment Due Soon' },
  [NotificationType.ASSIGNMENT_OVERDUE]: { icon: '🚨', color: '#ef4444', label: 'Assignment Overdue' },
  [NotificationType.ASSIGNMENT_GRADED]: { icon: '✅', color: '#10b981', label: 'Assignment Graded' },
  [NotificationType.ONLINE_CLASS_REMINDER]: { icon: '📹', color: '#ef4444', label: 'Class Reminder' },
  [NotificationType.ONLINE_CLASS_STARTING]: { icon: '🔴', color: '#ef4444', label: 'Class Starting' },
  [NotificationType.ONLINE_CLASS_STARTED]: { icon: '🎥', color: '#10b981', label: 'Class Started' },
  [NotificationType.ONLINE_CLASS_ENDED]: { icon: '📞', color: '#6b7280', label: 'Class Ended' },
  [NotificationType.NEW_LEARNING_MATERIAL]: { icon: '📚', color: '#22c55e', label: 'New Material' },
  [NotificationType.TEACHER_ANNOUNCEMENT]: { icon: '📢', color: '#10b981', label: 'Announcement' },
  [NotificationType.ATTENDANCE_MARKED]: { icon: '✓', color: '#10b981', label: 'Attendance Marked' },
  [NotificationType.ATTENDANCE_LATE]: { icon: '🕐', color: '#f59e0b', label: 'Late Arrival' },
  [NotificationType.ATTENDANCE_ABSENT]: { icon: '✗', color: '#ef4444', label: 'Absent' },
  [NotificationType.SCAN_ARRIVAL]: { icon: '🟢', color: '#10b981', label: 'Arrival' },
  [NotificationType.SCAN_DEPARTURE]: { icon: '🔵', color: '#22c55e', label: 'Departure' },
  [NotificationType.SCAN_BUS_BOARDING]: { icon: '🚌', color: '#f59e0b', label: 'Bus Boarding' },
  [NotificationType.SCAN_BUS_DROP_OFF]: { icon: '🚌', color: '#f59e0b', label: 'Bus Drop Off' },
  [NotificationType.SCAN_GENERAL]: { icon: '📍', color: '#6b7280', label: 'Scan' },
  [NotificationType.GRADE_CHANGE_APPROVED]: { icon: '🎖️', color: '#10b981', label: 'Grade Approved' },
  [NotificationType.GRADE_CHANGE_REJECTED]: { icon: '❌', color: '#ef4444', label: 'Grade Rejected' },
};

export const notificationService = {
  // Create notification
  async createNotification(parentId, type, title, message, relatedId = null, actionUrl = null, schoolId = null) {
    const id = crypto.randomUUID();
    // iOS decodes parentId/schoolId as UUID? — a non-UUID string drops the doc,
    // so resolve the parent's `uuid` field and omit non-UUID schoolId values.
    const resolvedParentId = await resolveParentUuid(parentId);
    const notification = {
      id,
      parentId: resolvedParentId,
      type,
      title,
      message,
      isRead: false,
      createdAt: toIsoString(new Date()),
      relatedId,
      actionUrl,
    };
    if (resolvedParentId !== parentId) {
      notification.parentUid = parentId; // keep the auth uid for web-side lookups
    }
    if (asUuid(schoolId)) {
      notification.schoolId = schoolId;
    }

    await setDoc(doc(db, NOTIFICATIONS_COLLECTION, id), notification);
    return notification;
  },

  // Load notifications for a parent
  async loadNotifications(parentId) {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('parentId', '==', parentId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  },

  // Mark as read
  async markAsRead(notificationId) {
    await updateDoc(doc(db, NOTIFICATIONS_COLLECTION, notificationId), { isRead: true });
  },

  // Mark all as read for a parent
  async markAllAsRead(parentId) {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('parentId', '==', parentId),
      where('isRead', '==', false)
    );
    const snapshot = await getDocs(q);
    const batch = snapshot.docs.map(d => updateDoc(doc(db, NOTIFICATIONS_COLLECTION, d.id), { isRead: true }));
    await Promise.all(batch);
  },

  // Delete notification
  async deleteNotification(notificationId) {
    await deleteDoc(doc(db, NOTIFICATIONS_COLLECTION, notificationId));
  },

  // Real-time listener
  onNotifications(parentId, callback) {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('parentId', '==', parentId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(notifications);
    });
  },

  // Get unread count
  async getUnreadCount(parentId) {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('parentId', '==', parentId),
      where('isRead', '==', false)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.length;
  },
};
